import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { listDiscoveredTools, type DiscoveredToolRow } from '../lib/api';
import { fmtDate } from '../lib/format';

/**
 * /w/:slug/app/discovered-tools — Shadow-IT / discovered AI tools list.
 *
 * Shows AI tools discovered across the workspace via signal ingestion
 * (browser extension, SSO logs, network telemetry). Lets governance
 * teams filter by status, see user counts, and route new discoveries
 * into the use-case intake workflow.
 */

type StatusFilter = 'all' | 'new' | 'sanctioned' | 'blocked_recommended' | 'dismissed';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'sanctioned', label: 'Sanctioned' },
  { value: 'blocked_recommended', label: 'Blocked (recommended)' },
  { value: 'dismissed', label: 'Dismissed' },
];

const STATUS_TONE: Record<string, 'accent' | 'neutral'> = {
  sanctioned: 'accent',
  new: 'accent',
};

export function DiscoveredTools() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? '';
  const prefix = workspaceCtx ? `/w/${slug}/app` : '/app';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const list = useWorkspaceQuery(
    (s) => queryKeys.discoveredTools(s, statusFilter),
    (s) => listDiscoveredTools(s, statusFilter === 'all' ? undefined : statusFilter),
  );

  const rows = useMemo(() => list.data?.tools ?? [], [list.data]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Discovered tools"
        subtitle="AI tools detected across your workspace. Route new discoveries into use-case intake for governance review."
      />

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'var(--surface)',
        }}
      >
        <Field id="dt-status" label="Status">
          <Select
            id="dt-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {list.loading ? (
        <Spinner label="Loading discovered tools" />
      ) : list.error ? (
        <Alert variant="error">{list.error}</Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No discovered tools"
          description="When AI tools are detected through signal ingestion, they appear here for triage."
        />
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {rows.map((tool) => (
            <ToolRow key={tool.tool_id} tool={tool} prefix={prefix} />
          ))}
        </ol>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ToolRow({ tool, prefix }: { tool: DiscoveredToolRow; prefix: string }) {
  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ display: 'grid', gap: '4px', minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
            {tool.name}
          </span>
          <Badge tone={STATUS_TONE[tool.status] ?? 'neutral'}>
            {tool.status.replace(/_/g, ' ')}
          </Badge>
        </div>
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {tool.user_count} user{tool.user_count === 1 ? '' : 's'}
          {' · '}First seen {fmtDate(tool.first_seen)}
          {' · '}Last seen {fmtDate(tool.last_seen)}
        </span>
        {tool.signal_kinds ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            Signals: {tool.signal_kinds}
          </span>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
        {tool.status === 'new' ? (
          <Link
            to={`${prefix}/use-cases`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px var(--space-3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-2xs)',
              fontWeight: 500,
              textDecoration: 'none',
              minHeight: 'var(--min-touch-target)',
            }}
          >
            Start intake
          </Link>
        ) : null}
      </div>
    </li>
  );
}
